import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth';
import WalletProvider from './components/WalletProvider';
import Layout from './components/Layout';
import InstallPWAPrompt from './components/InstallPWAPrompt';
import OnboardingWizard, { useOnboardingTrigger } from './components/OnboardingWizard';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import LandingPage from './pages/LandingPage';
import DashboardPage from './pages/DashboardPage';
import BotsPage from './pages/BotsPage';
import GridBotPage from './pages/GridBotPage';
import SmartTradePage from './pages/SmartTradePage';
import SellerVerifyPage from './pages/SellerVerifyPage';
import ConditionalStrategyPage from './pages/ConditionalStrategyPage';
import StressTestPage from './pages/StressTestPage';
import DcaSafetyBotPage from './pages/DcaSafetyBotPage';
import TopTradersPage from './pages/TopTradersPage';
import PositionSizingPage from './pages/PositionSizingPage';
import NewsPage from './pages/NewsPage';
import CoinDetailPage from './pages/CoinDetailPage';
import JournalPage from './pages/JournalPage';
import OutboundWebhooksPage from './pages/OutboundWebhooksPage';
import StrategyHeatmapPage from './pages/StrategyHeatmapPage';
import { CurrencyProvider } from './hooks/useCurrency';
import BotConfigPage from './pages/BotConfigPage';
import AnalyticsPage from './pages/AnalyticsPage';
import RiskIntelligencePage from './pages/RiskIntelligencePage';
import SettingsPage from './pages/SettingsPage';
import MarketTrendPage from './pages/MarketTrendPage';
import ForecastPage from './pages/ForecastPage';
import SignalPage from './pages/SignalPage';
import StrategyBuilderPage from './pages/StrategyBuilderPage';
import AlertsPage from './pages/AlertsPage';
import MultiChainPage from './pages/MultiChainPage';
import InstallAppPage from './pages/InstallAppPage';
import MarketplacePage from './pages/MarketplacePage';
import AdminPage from './pages/AdminPage';
import HelpPage from './pages/HelpPage';
import CopyTradingPage from './pages/CopyTradingPage';
import PaperTradingPage from './pages/PaperTradingPage';
import ReferralPage from './pages/ReferralPage';
import AdvertisePage from './pages/AdvertisePage';
import AdvertiseSuccessPage from './pages/AdvertiseSuccessPage';
import PricingPage from './pages/PricingPage';
import ComparisonPage from './pages/ComparisonPage';
import FeaturesPage from './pages/FeaturesPage';
import FuturesPage from './pages/FuturesPage';
import SwapPage from './pages/SwapPage';
import PortfolioAnalyticsPage from './pages/PortfolioAnalyticsPage';
import AlgoOrdersPage from './pages/AlgoOrdersPage';
import CexTradingPage from './pages/CexTradingPage';
import HelpCenterPage from './pages/HelpCenterPage';
import LearningLibraryPage from './pages/LearningLibraryPage';
import LearningPathsPage from './pages/LearningPathsPage';
import AcceptAdminInvitePage from './pages/AcceptAdminInvitePage';
import WebhookSettingsPage from './pages/WebhookSettingsPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import AggregatorPage from './pages/AggregatorPage';
import AcceptInvitePage from './pages/AcceptInvitePage';
import TaxHarvestPage from './pages/TaxHarvestPage';
import ReportsPage from './pages/ReportsPage';
import StrategyCoachPage from './pages/StrategyCoachPage';
import QuantLabPage from './pages/QuantLabPage';
import LpStudioPage from './pages/LpStudioPage';
import TokenScannerPage from './pages/TokenScannerPage';
import LaunchVolumeForecasterPage from './pages/LaunchVolumeForecasterPage';
import DexLiquidityOptimizerPage from './pages/DexLiquidityOptimizerPage';
import ListingReadinessPage from './pages/ListingReadinessPage';
import AiRiskAnalystPage from './pages/AiRiskAnalystPage';
import LaunchRoadmapPlannerPage from './pages/LaunchRoadmapPlannerPage';
import VolumeHealthPage from './pages/VolumeHealthPage';
import CompetitorTrackerPage from './pages/CompetitorTrackerPage';
import TokenomicsAdvisorPage from './pages/TokenomicsAdvisorPage';
import CommunityPage from './pages/CommunityPage';
import MyChannelEarningsPage from './pages/MyChannelEarningsPage';
import LiveFeedPage from './pages/LiveFeedPage';
import OfficialChannelsPage from './pages/OfficialChannelsPage';
import CommunityProfilePage from './pages/CommunityProfilePage';
import CommunityLeaderboardPage from './pages/CommunityLeaderboardPage';
import CommunityAchievementsPage from './pages/CommunityAchievementsPage';
import VerifiedProjectsPage from './pages/VerifiedProjectsPage';
import ChannelDiscoveryPage from './pages/ChannelDiscoveryPage';
import CommunityUserProfilePage from './pages/CommunityUserProfilePage';
import TokenAnalyzerPage from './pages/TokenAnalyzerPage';
import WalletProfilerPage from './pages/WalletProfilerPage';
import RebalancerPage from './pages/RebalancerPage';
import ArbitragePage from './pages/ArbitragePage';
import WalletSessionPage from './pages/WalletSessionPage';
import DexLimitOrdersPage from './pages/DexLimitOrdersPage';
import DexDcaPage from './pages/DexDcaPage';
import DexStopOrdersPage from './pages/DexStopOrdersPage';
import DexSmartTradePage from './pages/DexSmartTradePage';
import DexGridBotPage from './pages/DexGridBotPage';
import DexTvWebhooksPage from './pages/DexTvWebhooksPage';
import DexAlgoOrdersPage from './pages/DexAlgoOrdersPage';
import DexConditionalBotPage from './pages/DexConditionalBotPage';
import DexArbitragePage from './pages/DexArbitragePage';
import ForecastAccuracyPage from './pages/ForecastAccuracyPage';
import SignalChannelsPage from './pages/SignalChannelsPage';
import TaxStrategyPage from './pages/TaxStrategyPage';
import TaxReportPage from './pages/TaxReportPage';
import QuantDeskPage from './pages/QuantDeskPage';
import TokenomicsStudioPage from './pages/TokenomicsStudioPage';
import TokenomicsPublicReportPage from './pages/TokenomicsPublicReportPage';
import AuthCallback from './pages/AuthCallback';
import ScannerPage from './pages/ScannerPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import ContactPage from './pages/ContactPage';
import PublicDocsPage from './pages/PublicDocsPage';
import PublicGuidesPage from './pages/PublicGuidesPage';
import VsCompetitorPage from './pages/VsCompetitorPage';
import KeywordLandingPage from './pages/KeywordLandingPage';

// Users with tier === 'pending' have signed up but not yet completed Stripe
// Checkout (no card on file → no trial activated). They're locked out of every
// in-app route and bounced to /pricing where they choose a plan. The Stripe
// webhook flips their tier to the chosen plan with status=trialing once they
// complete checkout. Keeps the marketing CTAs ("Credit card required") honest
// and blocks the disposable-email free-trial loophole.
function isPendingTier(user) {
  return user?.tier === 'pending';
}

// Root: landing page when logged out, dashboard when logged in
function RootRoute() {
  const { isLoggedIn, user } = useAuth();
  if (!isLoggedIn) return <LandingPage />;
  if (isPendingTier(user)) return <Navigate to="/pricing" replace />;
  return <Layout><DashboardPage /></Layout>;
}

// Protected route wrapper
function ProtectedRoute({ children }) {
  const { isLoggedIn, user } = useAuth();
  if (!isLoggedIn) return <Navigate to="/login" replace />;
  // Pending users (signed up but never completed checkout) get bounced to
  // /pricing. They can still see /settings to access the subscription tab and
  // complete checkout — that's why /settings is explicitly allowed below.
  if (isPendingTier(user) && !window.location.pathname.startsWith('/settings')) {
    return <Navigate to="/pricing" replace />;
  }
  return <Layout>{children}</Layout>;
}

// Admin-only route wrapper. Lets through the legacy Owner (role === 'admin')
// AND anyone with at least one RBAC role assigned — those non-Owner role
// holders see only the tabs their permissions cover via AdminPage's filter.
function AdminRoute({ children }) {
  const { isLoggedIn, user } = useAuth();
  if (!isLoggedIn) return <Navigate to="/login" replace />;
  const isOwner = user?.role === 'admin' || (Array.isArray(user?.roles) && user.roles.includes('owner'));
  const hasRoles = Array.isArray(user?.roles) && user.roles.length > 0;
  if (!isOwner && !hasRoles) return <Navigate to="/" replace />;
  return <Layout>{children}</Layout>;
}

// Public route wrapper (redirects to dashboard if logged in)
function PublicRoute({ children }) {
  const { isLoggedIn } = useAuth();
  if (isLoggedIn) return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes — no auth required */}
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/comparison" element={<ComparisonPage />} />
      <Route path="/features" element={<FeaturesPage />} />
      <Route path="/advertise" element={<AdvertisePage />} />
      <Route path="/advertise/success" element={<AdvertiseSuccessPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/contact" element={<ContactPage />} />
      {/* Public mirror routes — same content lives in /help-center and /learning-library for logged-in members.
          The /docs/* and /guides/* mirrors are crawlable + soft-paywalled (full body in HTML for Google,
          first-fold + signup CTA for anonymous humans). Drives long-tail SEO. */}
      <Route path="/docs"           element={<PublicDocsPage />} />
      <Route path="/docs/:slug"     element={<PublicDocsPage />} />
      <Route path="/guides"         element={<PublicGuidesPage />} />
      <Route path="/guides/:slug"   element={<PublicGuidesPage />} />
      {/* High-intent keyword landing pages — SEO target: head queries like "best crypto trading bot" */}
      <Route path="/best-crypto-trading-bot" element={<KeywordLandingPage />} />
      <Route path="/dex-trading-bot"         element={<KeywordLandingPage />} />
      <Route path="/crypto-copy-trading"     element={<KeywordLandingPage />} />
      <Route path="/crypto-grid-bot"         element={<KeywordLandingPage />} />
      <Route path="/dca-bot"                 element={<KeywordLandingPage />} />
      <Route path="/cross-chain-arbitrage"   element={<KeywordLandingPage />} />
      <Route path="/automated-crypto-trading" element={<KeywordLandingPage />} />
      {/* Competitor comparison landing pages — SEO target: "X alternative" / "X vs haythix" */}
      <Route path="/vs/:competitor" element={<VsCompetitorPage />} />
      <Route path="/register" element={<RegisterPage />} />
      {/* Free public tools — no auth required */}
      <Route path="/analyze" element={<TokenAnalyzerPage />} />
      <Route path="/analyze/:address" element={<TokenAnalyzerPage />} />
      <Route path="/wallet" element={<WalletProfilerPage />} />
      <Route path="/wallet/:address" element={<WalletProfilerPage />} />
      <Route path="/tokenomics/r/:publicSlug" element={<TokenomicsPublicReportPage />} />
      <Route
        path="/login"
        element={<PublicRoute><LoginPage /></PublicRoute>}
      />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
      <Route path="/reset-password"  element={<ResetPasswordPage />} />
      <Route path="/verify-email"    element={<VerifyEmailPage />} />
      {/* Both URLs are kept so old invite emails still work */}
      <Route path="/accept-admin-invite" element={<AcceptAdminInvitePage />} />
      <Route path="/accept-team-invite"  element={<AcceptAdminInvitePage />} />
      {/* OAuth callback — public landing for the Google sign-in flow */}
      <Route path="/auth/callback"   element={<AuthCallback />} />

      {/* Protected routes */}
      <Route path="/"                element={<RootRoute />} />
      <Route path="/bots"            element={<ProtectedRoute><BotsPage /></ProtectedRoute>} />
      <Route path="/bots/new"        element={<ProtectedRoute><BotConfigPage /></ProtectedRoute>} />
      <Route path="/bots/:botId"     element={<ProtectedRoute><BotConfigPage /></ProtectedRoute>} />
      <Route path="/bots/:botId/analytics" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
      <Route path="/grid-bot"        element={<ProtectedRoute><GridBotPage /></ProtectedRoute>} />
      <Route path="/smart-trade"     element={<ProtectedRoute><SmartTradePage /></ProtectedRoute>} />
      <Route path="/seller-verify"   element={<ProtectedRoute><SellerVerifyPage /></ProtectedRoute>} />
      <Route path="/conditional-bots" element={<ProtectedRoute><ConditionalStrategyPage /></ProtectedRoute>} />
      <Route path="/stress-test"     element={<ProtectedRoute><StressTestPage /></ProtectedRoute>} />
      <Route path="/dca-safety"      element={<ProtectedRoute><DcaSafetyBotPage /></ProtectedRoute>} />
      <Route path="/top-traders"     element={<ProtectedRoute><TopTradersPage /></ProtectedRoute>} />
      <Route path="/position-sizing" element={<ProtectedRoute><PositionSizingPage /></ProtectedRoute>} />
      <Route path="/news"            element={<ProtectedRoute><NewsPage /></ProtectedRoute>} />
      <Route path="/coin"            element={<ProtectedRoute><CoinDetailPage /></ProtectedRoute>} />
      <Route path="/coin/:coinId"    element={<ProtectedRoute><CoinDetailPage /></ProtectedRoute>} />
      <Route path="/journal"         element={<ProtectedRoute><JournalPage /></ProtectedRoute>} />
      <Route path="/outbound-webhooks" element={<ProtectedRoute><OutboundWebhooksPage /></ProtectedRoute>} />
      <Route path="/strategy-heatmap"  element={<ProtectedRoute><StrategyHeatmapPage /></ProtectedRoute>} />
      <Route path="/analytics"       element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
      <Route path="/risk"            element={<ProtectedRoute><RiskIntelligencePage /></ProtectedRoute>} />
      <Route path="/settings"        element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/market-trend"    element={<ProtectedRoute><MarketTrendPage /></ProtectedRoute>} />
      <Route path="/forecast"        element={<ProtectedRoute><ForecastPage /></ProtectedRoute>} />
      <Route path="/signals"         element={<ProtectedRoute><SignalPage /></ProtectedRoute>} />
      <Route path="/strategy-builder"element={<ProtectedRoute><StrategyBuilderPage /></ProtectedRoute>} />
      <Route path="/alerts"          element={<ProtectedRoute><AlertsPage /></ProtectedRoute>} />
      <Route path="/multi-chain"     element={<ProtectedRoute><MultiChainPage /></ProtectedRoute>} />
      <Route path="/install-app"     element={<ProtectedRoute><InstallAppPage /></ProtectedRoute>} />
      <Route path="/marketplace"     element={<ProtectedRoute><MarketplacePage /></ProtectedRoute>} />
      <Route path="/copy-trading"    element={<ProtectedRoute><CopyTradingPage /></ProtectedRoute>} />
      <Route path="/paper-trading"   element={<ProtectedRoute><PaperTradingPage /></ProtectedRoute>} />
      <Route path="/futures"         element={<ProtectedRoute><FuturesPage /></ProtectedRoute>} />
      <Route path="/swap"            element={<ProtectedRoute><SwapPage /></ProtectedRoute>} />
      <Route path="/portfolio-analytics" element={<ProtectedRoute><PortfolioAnalyticsPage /></ProtectedRoute>} />
      <Route path="/rebalancer"       element={<ProtectedRoute><RebalancerPage /></ProtectedRoute>} />
      <Route path="/arbitrage"        element={<ProtectedRoute><ArbitragePage /></ProtectedRoute>} />
      <Route path="/wallet-sessions"  element={<ProtectedRoute><WalletSessionPage /></ProtectedRoute>} />
      <Route path="/dex-limit-orders" element={<ProtectedRoute><DexLimitOrdersPage /></ProtectedRoute>} />
      <Route path="/dex-dca"          element={<ProtectedRoute><DexDcaPage /></ProtectedRoute>} />
      <Route path="/dex-stop-orders"  element={<ProtectedRoute><DexStopOrdersPage /></ProtectedRoute>} />
      <Route path="/dex-smart-trade"  element={<ProtectedRoute><DexSmartTradePage /></ProtectedRoute>} />
      <Route path="/dex-grid-bot"     element={<ProtectedRoute><DexGridBotPage /></ProtectedRoute>} />
      <Route path="/dex-tv-webhooks"  element={<ProtectedRoute><DexTvWebhooksPage /></ProtectedRoute>} />
      <Route path="/dex-algo-orders"  element={<ProtectedRoute><DexAlgoOrdersPage /></ProtectedRoute>} />
      <Route path="/dex-conditional-bot" element={<ProtectedRoute><DexConditionalBotPage /></ProtectedRoute>} />
      <Route path="/dex-arbitrage"        element={<ProtectedRoute><DexArbitragePage /></ProtectedRoute>} />
      <Route path="/forecast-accuracy" element={<ProtectedRoute><ForecastAccuracyPage /></ProtectedRoute>} />
      <Route path="/signal-channels"  element={<ProtectedRoute><SignalChannelsPage /></ProtectedRoute>} />
      <Route path="/tax-strategy"     element={<ProtectedRoute><TaxStrategyPage /></ProtectedRoute>} />
      <Route path="/tax-report"       element={<ProtectedRoute><TaxReportPage /></ProtectedRoute>} />
      <Route path="/quant-desk" element={<ProtectedRoute><QuantDeskPage /></ProtectedRoute>} />
      <Route path="/premium-analytics" element={<ProtectedRoute><QuantDeskPage /></ProtectedRoute>} />
      <Route path="/tokenomics-studio" element={<ProtectedRoute><TokenomicsStudioPage /></ProtectedRoute>} />
      <Route path="/scanner"           element={<ProtectedRoute><ScannerPage /></ProtectedRoute>} />
      <Route path="/algo-orders"      element={<ProtectedRoute><AlgoOrdersPage /></ProtectedRoute>} />
      <Route path="/cex-trading"      element={<ProtectedRoute><CexTradingPage /></ProtectedRoute>} />
      <Route path="/help-center"      element={<ProtectedRoute><HelpCenterPage /></ProtectedRoute>} />
      <Route path="/learning-library" element={<ProtectedRoute><LearningLibraryPage /></ProtectedRoute>} />
      <Route path="/learning-paths"   element={<ProtectedRoute><LearningPathsPage /></ProtectedRoute>} />
      <Route path="/webhooks"         element={<ProtectedRoute><WebhookSettingsPage /></ProtectedRoute>} />
      <Route path="/referral"        element={<ProtectedRoute><ReferralPage /></ProtectedRoute>} />
      {/* Elite killer features */}
      <Route path="/aggregator"               element={<ProtectedRoute><AggregatorPage /></ProtectedRoute>} />
      <Route path="/tax-harvest"              element={<ProtectedRoute><TaxHarvestPage /></ProtectedRoute>} />
      <Route path="/reports"                  element={<ProtectedRoute><ReportsPage /></ProtectedRoute>} />
      <Route path="/strategy-coach"           element={<ProtectedRoute><StrategyCoachPage /></ProtectedRoute>} />
      <Route path="/quant-lab"                element={<ProtectedRoute><QuantLabPage /></ProtectedRoute>} />
      <Route path="/lp-studio"                element={<ProtectedRoute><LpStudioPage /></ProtectedRoute>} />
      <Route path="/token-scanner"            element={<ProtectedRoute><TokenScannerPage /></ProtectedRoute>} />
      <Route path="/accept-invite"            element={<AcceptInvitePage />} />
      <Route path="/launch-volume-forecaster" element={<ProtectedRoute><LaunchVolumeForecasterPage /></ProtectedRoute>} />
      <Route path="/dex-liquidity-optimizer"  element={<ProtectedRoute><DexLiquidityOptimizerPage /></ProtectedRoute>} />
      <Route path="/listing-readiness"        element={<ProtectedRoute><ListingReadinessPage /></ProtectedRoute>} />
      {/* Old /activity-simulation route — wash-trading planner removed 2026-05-19; redirect to the replacement */}
      <Route path="/activity-simulation" element={<ProtectedRoute><LaunchVolumeForecasterPage /></ProtectedRoute>} />
      <Route path="/ai-risk-analyst"     element={<ProtectedRoute><AiRiskAnalystPage /></ProtectedRoute>} />
      <Route path="/launch-roadmap"      element={<ProtectedRoute><LaunchRoadmapPlannerPage /></ProtectedRoute>} />
      {/* Old /campaign-scheduler URL — wash-trading planner removed 2026-05-19; redirect to replacement */}
      <Route path="/campaign-scheduler"  element={<ProtectedRoute><LaunchRoadmapPlannerPage /></ProtectedRoute>} />
      <Route path="/volume-health"       element={<ProtectedRoute><VolumeHealthPage /></ProtectedRoute>} />
      <Route path="/competitor-tracker"  element={<ProtectedRoute><CompetitorTrackerPage /></ProtectedRoute>} />
      <Route path="/tokenomics-advisor"  element={<ProtectedRoute><TokenomicsAdvisorPage /></ProtectedRoute>} />
      <Route path="/admin"           element={<AdminRoute><AdminPage /></AdminRoute>} />
      <Route path="/community"              element={<ProtectedRoute><CommunityPage /></ProtectedRoute>} />
      <Route path="/community/live-feed"    element={<ProtectedRoute><LiveFeedPage /></ProtectedRoute>} />
      <Route path="/community/official-channels" element={<ProtectedRoute><OfficialChannelsPage /></ProtectedRoute>} />
      <Route path="/community/profile"      element={<ProtectedRoute><CommunityProfilePage /></ProtectedRoute>} />
      <Route path="/community/leaderboard"  element={<ProtectedRoute><CommunityLeaderboardPage /></ProtectedRoute>} />
      <Route path="/community/achievements" element={<ProtectedRoute><CommunityAchievementsPage /></ProtectedRoute>} />
      <Route path="/community/projects" element={<ProtectedRoute><VerifiedProjectsPage /></ProtectedRoute>} />
      <Route path="/community/discover" element={<ProtectedRoute><ChannelDiscoveryPage /></ProtectedRoute>} />
      <Route path="/community/profile/:userId" element={<ProtectedRoute><CommunityUserProfilePage /></ProtectedRoute>} />
      <Route path="/community/my-earnings" element={<ProtectedRoute><MyChannelEarningsPage /></ProtectedRoute>} />
      <Route path="/help"            element={<ProtectedRoute><HelpPage /></ProtectedRoute>} />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// Only mount the onboarding hook when the user is logged in. Calling
// `useOnboardingTrigger` on the login page would fire `getWizardStatus()`
// against an expired token and trigger the 401 redirect loop.
function LoggedInOverlays() {
  const { shouldShow, dismiss, refresh } = useOnboardingTrigger();
  return (
    <>
      <InstallPWAPrompt />
      {shouldShow && <OnboardingWizard onClose={() => { dismiss(); refresh(); }} onComplete={() => { dismiss(); refresh(); }} />}
    </>
  );
}

function GlobalOverlays() {
  const { isLoggedIn } = useAuth();
  if (!isLoggedIn) return <InstallPWAPrompt />;
  return <LoggedInOverlays />;
}

export default function App() {
  return (
    <WalletProvider>
    <BrowserRouter>
      <AuthProvider>
        <CurrencyProvider>
          <AppRoutes />
          <GlobalOverlays />
        </CurrencyProvider>
      </AuthProvider>
    </BrowserRouter>
    </WalletProvider>
  );
}
